import type { QueryClient } from '@tanstack/react-query';

// Query keys
export const queryKeys = {
  me: ['me'] as const,
  workspaces: ['workspaces'] as const,
  workspace: (id: string) => ['workspaces', id] as const,
  brands: (workspaceId: string) => ['brands', workspaceId] as const,
  posts: (workspaceId: string, filters?: Record<string, string>) =>
    ['posts', workspaceId, filters] as const,
  stats: (workspaceId: string) => ['stats', workspaceId] as const,
  connections: (workspaceId: string) => ['connections', workspaceId] as const,
  brandConnections: (workspaceId: string, brandId: string) =>
    ['brandConnections', workspaceId, brandId] as const,
  apiKeys: (workspaceId: string) => ['apiKeys', workspaceId] as const,
  dataEvents: (workspaceId: string) => ['dataEvents', workspaceId] as const,
  brandKnowledge: (workspaceId: string, brandId: string) =>
    ['brandKnowledge', workspaceId, brandId] as const,
  review: (workspaceId: string) => ['review', workspaceId] as const,
  cadences: (workspaceId: string) => ['cadences', workspaceId] as const,
  analyticsOverview: (workspaceId: string, from?: string, to?: string) =>
    ['analyticsOverview', workspaceId, from, to] as const,
  postAnalytics: (workspaceId: string, sort?: string, dir?: string, page?: number, size?: number) =>
    ['postAnalytics', workspaceId, sort, dir, page, size] as const,
  postTimeseries: (workspaceId: string, postId: string, days: number) =>
    ['postTimeseries', workspaceId, postId, days] as const,
  adCampaigns: (workspaceId: string) => ['adCampaigns', workspaceId] as const,
  adCampaign: (workspaceId: string, campaignId: string) =>
    ['adCampaign', workspaceId, campaignId] as const,
  campaignInsights: (workspaceId: string, campaignId: string) =>
    ['campaignInsights', workspaceId, campaignId] as const,
  // V48 — billingStatus key removed; useBillingStatus(workspaceId) uses
  // ['billing-status', workspaceId] inline so each workspace has its own row.
  adminUsers: (params?: { search?: string; plan?: string; status?: string }) =>
    ['adminUsers', params] as const,
  adminUsage: ['adminUsage'] as const,
};

/**
 * V160 — refetchInterval factory that polls fast (3s) while ANY post in the
 * result is mid copy-regeneration, then drops back to `idleMs` once they all
 * settle. This is what makes the "Regenerating…" card flip to the new copy on
 * its own — no hard refresh, and it self-recovers even if the user navigated
 * away and back (the status lives on the row, not in a mutation flag).
 * Handles both bare arrays (usePosts/useReviewPosts) and `{ items }` paged
 * responses.
 */
export const regeneratingAwareInterval =
  (idleMs: number | false) =>
  (query: { state: { data: unknown } }): number | false => {
    const data = query.state.data;
    const list = (
      Array.isArray(data) ? data : ((data as { items?: unknown })?.items ?? [])
    ) as Array<{ copyGenerationStatus?: string | null }>;
    return list.some((p) => p?.copyGenerationStatus === 'regenerating') ? 3000 : idleMs;
  };

/**
 * Invalidate EVERY list that renders a PostCard so a review action
 * (approve / reject / regenerate / publish / republish / visual) refreshes
 * the card on its own — no hard refresh. Three independent query keys feed
 * post cards: the review inbox (`review`), the simple grids/overview
 * (`posts`), and the paginated Posts page (`posts-paged`). A mutation that
 * misses any one leaves a stale card — exactly the bug where publish/approve
 * didn't update the Posts grid until a full reload.
 */
export const invalidatePostLists = (qc: QueryClient, workspaceId: string) => {
  qc.invalidateQueries({ queryKey: queryKeys.review(workspaceId) });
  qc.invalidateQueries({ queryKey: ['posts', workspaceId] });
  qc.invalidateQueries({ queryKey: ['posts-paged', workspaceId] });
};
